import React, { useState, useEffect, useRef } from 'react';
import crmLabels from '../utils/crmLabels';

const axios = require('axios').default;

// Controlled picker for a CRM "party" — a contact (fan) OR an organization.
// Searches both endpoints in parallel and shows one merged dropdown with a
// type badge. value: { type: 'fan' | 'organization', id, name } | null.
// onChange receives the same shape (or null). Generalizes FanAutocomplete for
// callers (soft credits) whose recipient can be either kind.
export default function CrmPartyAutocomplete({
  team,
  fanOnly = false,
  omitFanIds = [],
  omitOrganizationIds = [],
  value,
  onChange,
  placeholder,
  testId,
}) {
  const labels = crmLabels(team);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!team || !team.id || value) return undefined;
    const term = query.trim();
    if (!term) { setResults([]); return undefined; }

    let ignore = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      const fanParams = { name: term };
      if (omitFanIds.length > 0) fanParams.omit_ids = omitFanIds;

      const [fans, orgs] = await Promise.all([
        axios.get(`/teams/${team.id}/crm/fans/autocomplete`, { params: fanParams })
          .then((res) => res.data || []).catch(() => []),
        fanOnly ? Promise.resolve([]) : axios.get(`/teams/${team.id}/crm/organizations/search`, { params: { q: term } })
          .then((res) => res.data || []).catch(() => []),
      ]);
      if (ignore) return;

      setResults([
        ...fans.map((f) => {
          const name = fanOnly ? (f.name || f.email) : f.name;
          const preview = fanOnly && f.title_email_preview === name ? null : f.title_email_preview;
          return { type: 'fan', id: f.id, name, preview };
        }),
        ...orgs
          .filter((o) => !omitOrganizationIds.includes(o.id))
          .map((o) => ({ type: 'organization', id: o.id, name: o.name })),
      ]);
      setOpen(true);
      setLoading(false);
    }, 250);

    return () => { ignore = true; clearTimeout(handle); };
  }, [team?.id, fanOnly, query, value, omitFanIds.join(','), omitOrganizationIds.join(',')]);

  if (value) {
    return (
      <span className="d-inline-flex align-items-center" style={{ gap: '6px' }} data-testid={testId}>
        {value.type === 'organization' && <i className="fal fa-building text-muted" aria-hidden="true" />}
        <span>{value.name}</span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-muted"
          aria-label="Remove selection"
          onClick={() => { onChange(null); setQuery(''); }}
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <i className="fa fa-times" />
        </button>
      </span>
    );
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="ldm-input"
        value={query}
        placeholder={placeholder || (fanOnly ? `Search ${labels.contact.plural.toLowerCase()}…` : `Search contacts or ${labels.organization.plural.toLowerCase()}…`)}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        data-testid={testId}
      />
      {open && (loading || results.length > 0) && (
        <ul
          className="list-group position-absolute w-100"
          style={{ zIndex: 1060, maxHeight: 240, overflowY: 'auto' }}
        >
          {loading && <li className="list-group-item text-muted">Searching…</li>}
          {!loading && results.map((r) => (
            <li key={`${r.type}-${r.id}`}>
              <button
                type="button"
                className="list-group-item list-group-item-action w-100 text-start d-flex justify-content-between align-items-center"
                onClick={() => {
                  onChange({ type: r.type, id: r.id, name: r.name });
                  setQuery('');
                  setResults([]);
                  setOpen(false);
                }}
                data-testid={testId ? `${testId}-option-${r.type}-${r.id}` : undefined}
              >
                <span>
                  <div>{r.name}</div>
                  {r.preview && <small className="text-muted">{r.preview}</small>}
                </span>
                {!fanOnly && (
                  <span className="badge bg-light text-muted text-uppercase" style={{ fontSize: '10px' }}>
                    {r.type === 'organization' ? labels.organization.singular : labels.contact.singular}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
